import { TypedDocumentNode, OperationVariables } from "@apollo/client";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { SubmissionVirtualEventsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef, FilterKey } from "hooks/useOmnisearchDatagrid";
import React, { memo, useCallback } from "react";

import type { Row } from "./types";

import { mergeDefaultAndOmnisearch } from "../Manifests/mergeDefaultAndOmnisearch";
import { SubmissionsEmptyStateOverlay } from "./SubmissionsEmptyState";

// Module-level slots constant - stable reference, no re-renders
// The overlay component uses hooks internally to read state
// Provide both overlays: noRowsOverlay (no data) and noResultsOverlay (filtering returns nothing)
const GRID_SLOTS = {
  noRowsOverlay: SubmissionsEmptyStateOverlay,
  noResultsOverlay: SubmissionsEmptyStateOverlay,
};

// Grid column fields for MUI sort indicators (must match column `field` values).
// API sort fields are mapped in buildVariables below.
const INITIAL_SORT_MODEL: GridSortModel = [
  { field: "deadline", sort: "asc" },
  { field: "title", sort: "asc" },
];

/** Map grid sort fields to submissionVirtualEvents API sort fields */
const API_SORT_FIELD_MAP: Record<string, string> = {
  deadline: "deadline",
  dueDate: "deadline",
  title: "submission.submissionType",
  "submission.submissionType": "submission.submissionType",
};

function toApiSortModel(
  sort: GridSortModel
): { field: string; sort: "asc" | "desc" | null | undefined }[] {
  return sort.map((s) => ({
    field: API_SORT_FIELD_MAP[s.field] ?? s.field,
    sort: s.sort,
  }));
}

interface GridSectionProps {
  title: string;
  description: string;
  persistenceKey: string;
  columns: OmnisearchGridColDef<Row>[];
  dataQuery: TypedDocumentNode<SubmissionVirtualEventsQuery, OperationVariables>;
  defaultSearch: string;
  getItems: (data: SubmissionVirtualEventsQuery) => Row[];
  getCount: (data: SubmissionVirtualEventsQuery) => number;
  checkboxSelection: boolean;
  onRowClick: (params: GridRowParams<Row>) => void;
  getRowLink?: (row: Row) => string | null;
  commandButtons: React.ReactNode[];
  initialColumnVisibilityModel?: Record<string, boolean>;
  mobileColumnVisibilityModel?: Record<string, boolean>;
  additionalFilterKeys: FilterKey[];
  excludeFilterColumns: string[];
  filterGroups: FilterGroup[];
  tenantId: string | undefined;
}

/**
 * Memoized grid section for waste submissions.
 *
 * Uses OmnisearchDataGridV2 for optimized rendering.
 * Grid auto-wires to GridSelectionProvider via context - no apiRef prop needed.
 */
export const GridSection = memo(function GridSection({
  title,
  description,
  persistenceKey,
  columns,
  dataQuery,
  defaultSearch,
  getItems,
  getCount,
  checkboxSelection,
  onRowClick,
  getRowLink,
  commandButtons,
  initialColumnVisibilityModel,
  mobileColumnVisibilityModel,
  additionalFilterKeys,
  excludeFilterColumns,
  filterGroups,
  tenantId,
}: GridSectionProps) {
  // Layer defaultSearch (tenantId, submissionStatus, dueDate) under user omnisearch.
  // Keys present in omnisearch override the same keys from defaultSearch.
  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const search = mergeDefaultAndOmnisearch(defaultSearch, omnisearch);

      return {
        search,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: toApiSortModel(sort),
      };
    },
    [defaultSearch]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        pb: 3,
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="h5" component="h5" color="text.primary" pb={1}>
              {title}
            </Typography>
            <Typography fontSize={12} component="p">
              {description}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box>
        <OmnisearchDataGridV2
          query={dataQuery}
          persistenceKey={persistenceKey}
          columns={columns}
          getItems={getItems}
          getCount={getCount}
          buildVariables={buildVariables}
          checkboxSelection={checkboxSelection}
          onRowClick={onRowClick}
          getRowLink={getRowLink}
          commandButtons={commandButtons}
          initialPageSize={50}
          initialSortModel={INITIAL_SORT_MODEL}
          rowHeight="auto"
          initialColumnVisibilityModel={initialColumnVisibilityModel}
          mobileColumnVisibilityModel={mobileColumnVisibilityModel}
          additionalFilterKeys={additionalFilterKeys}
          excludeFilterColumns={excludeFilterColumns}
          filterGroups={filterGroups}
          tenantId={tenantId}
          slots={GRID_SLOTS}
        />
      </Box>
    </Paper>
  );
});
